/**
 * KOA-17362 — resolve the VALUE SOURCE of a workflow step's mapped OUTPUT.
 *
 * `MAP_OUTPUT` only declares WHICH workflow parameter a step writes. The value
 * itself comes from a symbol stored on `step.outputParameters[].value`, and a
 * freshly-mapped output gets a fixed-value symbol with NO value
 * (`workflow-provider.tsx SetStepParamsAndMappings` — correct for the panel,
 * where the user picks the source next). On a SYSTEM step nobody ever picks it,
 * so codegen's `Fixed` branch (`WorkflowStepGenerator.SetOutputs`) emits
 * `= ""` and the parameter is empty for the rest of the process — the exact
 * failure behind the smoke-12 `ReturnRequestId` / `ReturnRecordId` outputs, which
 * every downstream `WHERE ProcessInstanceId = …` step then keyed on.
 *
 * A system step's output can only come from one place: the RESULT of an action
 * attached to that step. This module maps a parameter to a concrete
 * (action, result field) pair — either from an explicit `FROM` clause or, when
 * the match is unambiguous, automatically.
 *
 * ANCHORED, never blanket. Auto-binding happens only on evidence:
 *   1. exactly ONE (action, field) pair whose field name equals the parameter
 *      name (case-insensitive), or
 *   2. the step has exactly ONE action, the parameter name ends in `Id`, and that
 *      action's result exposes an `Id` field — the "just-inserted row's key" shape.
 * Anything else resolves to `none`: the mapping still happens, the value stays
 * unbound, and `kuika/step-output-unbound` reports it rather than shipping an
 * always-empty parameter. Guessing "the first field" would silently bind the
 * wrong column, which is worse than an empty one.
 *
 * Pure and side-effect free so it can be unit-tested without a modeler.
 */

/**
 * What the action's statement DOES, when the definition says so.
 *
 * KOA-17362 §22 — the two are not interchangeable when the result has no fields.
 * The platform classifies a DB action at SAVE time from its result shape
 * (`DatasourceService.Internal_SaveAction`): a statement that yields columns is
 * stored as a reader, one that yields none as a non-query whose `ReturnTypeName`
 * is set to `Int32` — the ROWS-AFFECTED count. So a `write` with no fields has no
 * value to give, while a `read` with no fields is a genuine scalar. Codegen cannot
 * tell them apart (`BuildSingleResultFieldAccessor` emits the bare
 * `result.<Action>` either way), so a `MAP_OUTPUT … FROM <insert>` handed the
 * process `1` as the key of the row it had just written.
 */
export type OutputSourceStatementKind = "read" | "write";

/** One action attached to the step, flattened to just what source resolution needs. */
export interface IOutputSourceAction {
  /** Action definition id — `SetOutputs` finds the step's action by this. */
  readonly definitionId: string;
  /** Instance id of the action on the step (`step.actions[].id`). */
  readonly instanceId?: string;
  /** Display name, used for matching a `FROM` clause and for the panel description. */
  readonly displayName: string;
  /** The definition's raw name, also accepted by a `FROM` clause. */
  readonly definitionName?: string;
  /** Fields of the action's result type, in declaration order. Empty for a scalar result. */
  readonly resultFields: readonly IOutputSourceField[];
  /**
   * Insert/update/delete vs select, from the definition's statement type. Left
   * undefined when the definition does not classify itself (non-DB actions such
   * as SendMail), which keeps resolution exactly as permissive as before for them.
   */
  readonly statementKind?: OutputSourceStatementKind;
}

export interface IOutputSourceField {
  readonly name: string;
  readonly type?: string;
}

export interface IOutputSourceRequest {
  /** Name of the workflow parameter being written. */
  readonly parameterName: string;
  /** Declared type of the workflow parameter, when known. */
  readonly parameterType?: string;
  /** `FROM <actionName>` — explicit action, if the script named one. */
  readonly sourceActionName?: string;
  /** `FROM <actionName>.<field>` — explicit field, if the script named one. */
  readonly sourceFieldName?: string;
}

export interface IOutputSourceBound {
  readonly kind: "bound";
  readonly actionDefinitionId: string;
  readonly actionDisplayName: string;
  readonly resultActionId?: string;
  readonly fieldName?: string;
  readonly fieldType?: string;
  /** True when nothing in the script named this source — used to phrase the result message. */
  readonly inferred: boolean;
}

export type OutputSourceResolution =
  | IOutputSourceBound
  | { readonly kind: "unknown-action"; readonly available: readonly string[] }
  | { readonly kind: "unknown-field"; readonly actionDisplayName: string; readonly available: readonly string[] }
  | { readonly kind: "ambiguous"; readonly candidates: readonly string[] }
  /** The named action WRITES and returns no result set — its bare result is a row count. */
  | { readonly kind: "write-without-result"; readonly actionDisplayName: string }
  | { readonly kind: "none" };

function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

function findField(action: IOutputSourceAction, fieldName: string): IOutputSourceField | undefined {
  return action.resultFields.find((f: IOutputSourceField): boolean => sameName(f.name, fieldName));
}

function bind(
  action: IOutputSourceAction,
  field: IOutputSourceField | undefined,
  inferred: boolean
): IOutputSourceBound {
  const bound: IOutputSourceBound = {
    kind: "bound",
    actionDefinitionId: action.definitionId,
    actionDisplayName: action.displayName,
    resultActionId: action.instanceId,
    fieldName: field?.name,
    fieldType: field?.type,
    inferred
  };
  return bound;
}

/**
 * Resolve which action result a step output takes its value from. See the module
 * comment for the anchoring rules.
 */
export function resolveOutputSource(
  actions: readonly IOutputSourceAction[],
  request: IOutputSourceRequest
): OutputSourceResolution {
  if (actions.length === 0) return { kind: "none" };

  const explicitAction: string = (request.sourceActionName ?? "").trim();
  if (explicitAction.length > 0) {
    const action: IOutputSourceAction | undefined = actions.find(
      (a: IOutputSourceAction): boolean =>
        sameName(a.displayName, explicitAction) || sameName(a.definitionName ?? "", explicitAction)
    );
    if (!action) {
      return {
        kind: "unknown-action",
        available: actions.map((a: IOutputSourceAction): string => a.displayName)
      };
    }

    const explicitField: string = (request.sourceFieldName ?? "").trim();
    if (explicitField.length > 0) {
      const field: IOutputSourceField | undefined = findField(action, explicitField);
      if (!field) {
        return {
          kind: "unknown-field",
          actionDisplayName: action.displayName,
          available: action.resultFields.map((f: IOutputSourceField): string => f.name)
        };
      }
      return bind(action, field, false);
    }

    // `FROM <action>` with no field. A scalar-returning action IS the value; a
    // structured result needs a field, and the parameter's own name is the only
    // defensible guess — otherwise say which fields exist.
    if (action.resultFields.length === 0) {
      // KOA-17362 §22 — except when the action WRITES. An insert/update whose
      // statement declares no OUTPUT returns rows-affected, so binding its bare
      // result hands the process `1` where it asked for the key of the row just
      // inserted. Live smoke 15 did exactly this on the FIRST system step, and
      // every later `WHERE … = @Id` in the chain then matched nothing — a process
      // that builds clean, lints clean, and is dead at run time. Refusing here is
      // the only place the two cases are still distinguishable.
      if (action.statementKind === "write") {
        return { kind: "write-without-result", actionDisplayName: action.displayName };
      }
      return bind(action, undefined, false);
    }
    const byParamName: IOutputSourceField | undefined = findField(action, request.parameterName);
    if (byParamName) return bind(action, byParamName, false);
    return {
      kind: "unknown-field",
      actionDisplayName: action.displayName,
      available: action.resultFields.map((f: IOutputSourceField): string => f.name)
    };
  }

  // ── No FROM clause: anchored inference only ──────────────────────────────
  interface PairCandidate {
    readonly action: IOutputSourceAction;
    readonly field: IOutputSourceField;
  }
  const byName: PairCandidate[] = [];
  actions.forEach((action: IOutputSourceAction): void => {
    const field: IOutputSourceField | undefined = findField(action, request.parameterName);
    if (field) byName.push({ action, field });
  });
  if (byName.length === 1) return bind(byName[0].action, byName[0].field, true);
  if (byName.length > 1) {
    return {
      kind: "ambiguous",
      candidates: byName.map((c: PairCandidate): string => `${c.action.displayName}.${c.field.name}`)
    };
  }

  // The "key of the row this step just wrote" shape: one action, a parameter
  // named …Id, and an Id field on the result.
  const endsWithId: boolean = /id$/i.test(request.parameterName);
  if (actions.length === 1 && endsWithId) {
    const idField: IOutputSourceField | undefined = findField(actions[0], "Id");
    if (idField) return bind(actions[0], idField, true);
  }

  return { kind: "none" };
}
